import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import {
  BringToFront,
  CheckCircle2,
  Circle,
  Eye,
  EyeOff,
  LayoutTemplate,
  Pencil,
  RotateCw,
  SendToBack,
  X,
} from 'lucide-react';
import {
  STICKER_FRONT_RENDER_PX,
  clampFrontStickerPlacement,
  type BoardFaceLinkCardSurface,
  type BoardFaceList,
  type CardAgingMode,
  type CardAgingStage,
  type CardMirror,
  type PlacedSticker,
  type StickerUpdateBody,
} from '@molt/shared';
import type { BoardCardOpenIntent } from '@/lib/board-card-return';
import {
  dueStatus as resolveDueStatus,
  dueStatusLabel,
  formatCardDate,
  type CardDateTranslate,
  type DueStatus,
} from '@/lib/card-dates';
import { cardDisplayTitle } from '@/lib/card-title';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { formatLocaleCardDueDate } from '@/lib/locale-formatting';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';
import { coverVisual } from '@/lib/card-cover';
import type { DragHandle } from '@/lib/list-drag';
import { isOptimisticCardId } from '@/lib/cards';
import { cn } from '@/lib/cn';
import { frontLabels } from '@/lib/label-display';
import type { PowerUpSurfaceSummary } from '@/lib/power-ups';
import { stickerImageSrc, stickerLabel } from '@/lib/stickers';
import { CardSurface } from '../CardSurface';
import { IconButton } from '../IconButton';
import { LabelChip, labelChipName } from '../LabelChip';
import { profileName } from '../Avatar';
import { CardBadges, customFieldBadgeValue } from './CardBadges';
import { LinkCardChrome } from './LinkCardChrome';
import { MirrorDisplayControls } from './MirrorDisplayControls';

/**
 * The Phase 1 card front — `C1-BRIEF.md` §2/§7.3, agent D of chunk `C1`, and
 * the first real test of `FE-04.12`'s density budget (see
 * `apps/web/src/lib/density.ts`, whose `PROVISIONAL` inputs this file makes
 * checkable for the first time).
 *
 * `implementation-plan.md`'s B13 note is the scope, verbatim: **"Labels and
 * member avatars are the WHOLE visual vocabulary of a Phase 1 card front;
 * nothing else gets a colour."** So this renders exactly three things — the
 * card's **title**, its **labels** (`card.labels`, already in the board's
 * label-set order — `LabelChip`) and its **members** (`card.members` —
 * `AvatarStack`) — and nothing else. `cover`, `dueAt`/`startAt`,
 * `checklistItemCount`/`checklistCheckedItemCount`, `attachmentCount`,
 * `commentCount` is the E3 badge that `FR-CARD-034` requires on the card
 * front. `customFieldBadges` and `voteCount` were added later by B56/B59 and
 * render only through the compact neutral badge strip.
 *
 * `card` is `BoardFaceList['cards'][number]`: a union of the plain
 * `boardFaceCardSchema` and the three-way `surfaceType` discriminated union
 * (`card` / `mirror` / `link_card`). This component never narrows on
 * `surfaceType` and renders all three identically — a mirror's source-board
 * chip and a link card's target chrome are later blocks' surfaces, not this
 * one's.
 *
 * ⚠ **`AvatarStack`'s overflow chip is disabled here — this is the request
 * `Avatar.tsx`'s own docstring pre-authorised, not a silent workaround.**
 * `AvatarStack` renders a real `<button>` once `members.length` exceeds
 * `max`, by design — its docstring's own words: *"Shipping only the
 * focusable form means the card-front chunk has to ask for the other one
 * and say why, rather than reaching for it by default."* This is that
 * chunk, and the why is `LabelChip`'s own nested-`<button>` argument applied
 * one primitive over — see "THE ANCHOR" below.
 *
 * ✅ **Answered in the same chunk, and the interim is corrected in place.**
 * This file first set `max={card.members.length}` so the chip could never
 * render — every member shown, uncapped, which spills sideways on a crowded
 * card because `AvatarStack`'s root is `inline-flex` with no wrap. The parent
 * then added `overflowAction='none'` to the primitive, which is what actually
 * closes it: the `+3` chip renders with no tab stop, and the count still
 * reaches a screen reader as a sentence rather than as a bare `+3`. So the
 * default `max` is back in force and the row is bounded again.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * `C3-BRIEF.md` §6.4 — WHAT CHANGED, chunk `C3` agent E
 * ─────────────────────────────────────────────────────────────────────────
 *
 * ⚠ **THE ANCHOR IS NO LONGER THE ROOT.** C1 shipped `<CardSurface as="a"
 * href={card.path}>` as the whole tile. §7.4 now puts a second, focusable
 * control — the quick-edit pencil — on this same front, and the identical
 * `AvatarStack` argument two paragraphs up applies to it verbatim: a
 * `<button>` nested inside an `<a>` is invalid HTML, and a click on it
 * bubbles to the anchor and navigates instead of opening quick edit. Unlike
 * the avatar overflow chip, the pencil cannot be disabled away — §7.4 makes
 * it a required control.
 *
 * The fix is a "stretched link": the root is now a plain `<div>`
 * (`CardSurface as="div"`, so `variant`/`interactive` render identically —
 * only the tag changes), and the link lives as an EMPTY `absolute inset-0`
 * `<a>`, first in DOM order, carrying `href` and the explicit `aria-label`
 * and nothing else. It has no descendants, so there is no risk of any
 * content folding into its accessible name — if anything this is more robust
 * than C1's version, which relied on `aria-label` to override content that
 * WAS there. The visible label/title/avatar rows render as ordinary
 * (non-positioned) siblings after it, so they are unaffected in size or
 * position — with ONE later exception: member initials lifts the avatar row
 * alone to `relative z-[3]` so a hover reaches its tooltips, and forwards the
 * row's click, middle click, right-click and drag start to this link (`memberRowProps`
 * below; `docs/notes/member-initials.md`). It is still not a child of the link
 * and adds no tab stop. `getByRole('link', { name })` still finds this element by role
 * and name regardless of where in the tree it sits, so every one of C1's
 * existing assertions is unaffected. The pencil renders last, also
 * `absolute`, so it paints above the stretched link at the same stacking
 * level (later DOM order wins among siblings with no explicit z-index) and
 * intercepts its own clicks instead of triggering navigation.
 *
 * ⚠ **AN OPTIMISTIC CARD RENDERS NEITHER** (§5.3, §5.4). `shortLink`,
 * `cardNumber` and `path` are server-generated; a placeholder's `path` is
 * `''`, and `CardSurface as="a" href=""` would be a link to nowhere. Gated on
 * `isOptimisticCardId(card.id)` (`lib/cards.ts`, agent A — currently a stub
 * that throws; this file's own tests mock it, see the test file's header).
 * An optimistic card is a `div` with no inner link, `aria-busy`, no drag
 * attributes and no quick-edit affordances at all.
 *
 * ⚠ **THE DRAG HANDLE IS THE WHOLE ROOT**, not a sub-element the way
 * `ListHeader`'s grip button is for a list — `FR-BOARD-025`'s "anywhere on
 * its front". `dragHandle.setActivatorNodeRef` is the root `<div>`'s ref, and
 * `attributes`/`listeners` spread onto that same element — the same pattern
 * `ListHeader.tsx` uses for its handle button, just scaled to the whole card.
 * ⚠ **This does change the card's exposed ARIA role** while a `dragHandle` is
 * present: `dragHandle.attributes.role` is `'button'` (dnd-kit's own
 * convention, verified against `SortableListColumn.test.tsx`), and an
 * explicit `role` attribute on an element overrides its implicit one — so a
 * draggable card is exposed to assistive tech as a button, not a link, even
 * though the inner stretched element is still a real `<a href>` that Enter
 * still follows. Flagged in the return rather than "fixed" unilaterally: the
 * brief fixes the handle's placement (§6.4, "the drag handle goes on the
 * tile ROOT") and this is a direct, unavoidable consequence of that.
 *
 * ⚠ **`touch-none` is deliberately NOT applied here, unlike `ListHeader`'s
 * grip button, and this is a considered default rather than an oversight.**
 * `ListHeader.tsx`'s own comment already warns the class must never sit on a
 * container that also needs to scroll; for a card the tension is not merely
 * sharper, it is decisive, because the whole card front — not a small grip
 * icon — would carry it, and cards fill almost the entire height of a
 * scrollable list. WITH it, a touch swipe that starts on ANY card can no
 * longer scroll the list under it at all — a guaranteed, product-wide
 * regression that `FR-VIEW-010`'s 200-card list already depends on not
 * happening. The conclusion has held through three chunks and still holds.
 *
 * ✅ **`FE-40.7` UPDATE (`FR-VIEW-033` AC2): the omission now costs NOTHING,
 * and the reason this comment previously gave for the cost was WRONG.** The
 * paragraph above used to read that "without it, dnd-kit's `PointerSensor`
 * cannot begin a touch drag at all … `PointerSensor` needs the browser's own
 * touch-panning suppressed to claim the gesture". Measured in
 * `@dnd-kit/core` 6.3.1 `dist/core.esm.js` rather than recalled, that is not
 * what happens. `PointerSensor.activators`' handler rejects only
 * `!event.isPrimary || event.button !== 0`, and a first finger is primary with
 * button 0 — so it **did** claim the gesture. It was then killed by the
 * browser's own scroll: `pointermove` during a native pan is **non-cancelable**,
 * so `AbstractPointerSensor.handleMove`'s `if (event.cancelable)
 * event.preventDefault()` is a no-op and `pointercancel` lands in
 * `handleCancel`. Same symptom, different cause — and the wrong cause is what
 * named `touch-action: none` as the fix for three chunks.
 *
 * The real fix was one file earlier, in `lib/dnd.ts`: a `PointerSensor` claims
 * touch, so a `TouchSensor` beside it is inert at any array position
 * (`bindActivatorToSensorInstantiator` early-returns once `activeRef.current`
 * is set). `useMoltSensors()` now registers a mouse sensor and a **`TouchSensor`
 * with a `{ delay: 250, tolerance: 5 }` long-press constraint** instead of one
 * `PointerSensor`. `TouchSensor.setup()` installs a window-level `touchmove`
 * listener with `{ passive: false }` expressly so `preventDefault()` works in
 * dynamically added handlers, so the sensor buys its own suppression **at drag
 * time** rather than needing CSS to buy it **at rest**.
 *
 * So the card front keeps touch scrolling AND gains a touch drag, and this
 * class stays off. `FR-VIEW-033` AC2 is served by the delay; AC1 keeps the
 * list scrolling it would have lost. Nothing on this element changed —
 * the update is recorded here because this is where the wrong reason was
 * written down.
 *
 * ⚠ **The three quick-edit openers (§7.2–§7.4)** — right-click, the pencil,
 * and `e` — all call `onQuickEdit`, and all are withheld together whenever
 * `onQuickEdit` is `undefined` (no edit permission), the card is optimistic,
 * or quick edit is already open on this card (`quickEditing`). None of them
 * render or attach the panel itself — that is `QuickEdit.tsx`, agent D's.
 * ✅ Its own file header (landed after this file's props were fixed) settles
 * where: `SortableCardTile` swaps `QuickEdit` in for `CardTile` INSIDE THE
 * SAME `<li>`, in normal flow, not layered on top of it — "elevated" turned
 * out to mean `relative z-10`, not an absolute overlay. `quickEditing` never
 * reaches `true` on a mounted `CardTile` in the composed app for that reason
 * (the sibling swap replaces it outright); the `z-10` this file still applies
 * when `quickEditing` is true is defensive — for a caller that renders
 * `CardTile` directly with `quickEditing` set (as `CardTile.test.tsx` does,
 * deliberately) — and costs nothing when it never fires. See
 * `SortableCardTile.tsx`'s own header for the one real gap this uncovered:
 * `QuickEdit` needs an `onClose` that `C3-BRIEF.md` §6.4's fixed props do not
 * supply.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * Card-Front Projection contract chunk — what this file consumes
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Two of the chunk's three contract additions land here, and both are reads —
 * this file writes nothing and calls nothing new.
 *
 *   • **`card.stickers`** (`FE-58.1`, `FR-CARD-032`, `FR-EXP-027`) — the
 *     placements `GET /boards/:id/contents` now projects, drawn as an
 *     out-of-flow layer beneath the title. See the layer's own comment in the
 *     return below for the DOM-order rule that IS the requirement.
 *   • **`coversEnabled`** (`FE-25.2`, `FR-CARD-025` AC5) — a prop, not a field
 *     on the card: the setting is board-level (`boardContentsBoardSchema
 *     .coversEnabled`) and the composer threads it. See its docstring.
 *
 * The chunk's third addition, `card.watching` (`FE-29.1`/`FE-25.1`,
 * `FR-BOARD-030` AC4), is deliberately NOT consumed here. `FR-BOARD-030`
 * enumerates the badge strip in order — description → comments → attachments
 * → checklist → due → **watch** → member avatars — and that strip is
 * `CardBadges`, which this file renders and does not reimplement. The eye
 * belongs there, one component over, for the same reason every other badge
 * does.
 *
 * ────────────────────────────────────────────────────────────────────────
 * Card-Front Chrome contract chunk — what this file consumes (`FE-58.1`)
 * ────────────────────────────────────────────────────────────────────────
 *
 * `FR-CARD-032` AC1 and `FR-EXP-027` AC1 both read *"given the sticker tray
 * is open, when the user drags a sticker onto a card front"*. Fan-out 7
 * shipped the read half (`card.stickers`, above). This chunk makes the card
 * front **a drop target and a removal surface**, and both arrive as callbacks
 * — this file still performs no mutation and calls no route.
 *
 *   • **`onPlaceSticker`** — the drop half. `AUDIT.md` §7.1 fixes the shape as
 *     *"one `place(cardId, x, y)`, two ways in"*: the tray already knows
 *     WHICH sticker is armed (it has to, for the keyboard path), so the card
 *     front only ever answers **where**. No sticker id travels in the drop.
 *   • **`stickerDropActive`** — the accept ring, added by the *Card-Front
 *     Sticker Ring* chunk (`FE-58.1`, fan-out 12). The drop already worked
 *     before it and the ring already existed here; what did not exist was any
 *     path from the target that resolves the card to the element that draws.
 *     A one-way boolean is that path, and the ruling that put it here rather
 *     than on `SortableCardTile`'s `<li>` is written out on the prop.
 *   • **`onRequestRemoveSticker`** — `FR-EXP-027` AC2. It *requests*; it does
 *     not delete. AC2 is *"hovers it and selects remove **and confirms**"*
 *     and `FR-CARD-032` says sticker deletion *"has no undo … so it requires
 *     a confirmation"*, so the composer opens the same dialog the card back
 *     already ships (`cardBack.stickers.remove.title` / `.description`). A
 *     front control that deleted outright would fail AC2 while the card back
 *     passed — the two surfaces disagreeing about one act.
 *
 * ⚠ **The sticker drag is native HTML5 drag-and-drop, NOT dnd-kit, and that
 * is a decision rather than an omission.** `lib/card-drag.ts`'s
 * `boardCollisionDetection` opens with `if (!isCardDrag(args.active))` and
 * routes everything else into a list-only branch that has already filtered
 * every card droppable out — so a third dnd-kit drag typed `'sticker'` could
 * never resolve a card as its target and would silently land on a column.
 * Native DnD sidesteps that entirely and has in-file precedent
 * (`BoardFace.tsx`'s pinned-view reorder). The two mechanisms cannot observe
 * each other: dnd-kit's `PointerSensor` activates on `pointerdown` against the
 * activator node and registers no `dragstart`/`dragover`/`drop` listener at
 * all, while the native drag protocol fires no pointer events once a drag is
 * under way. **No `DragOverlay` participates**, so the recorded `rotate-2`
 * keyboard-drag defect cannot apply to it.
 */

/**
 * The `dataTransfer` type that marks a drag as *a sticker being placed*.
 *
 * ⚠ **THERE ARE TWO EXPORTED NAMES FOR THIS ONE MEDIA TYPE, AND A TEST HOLDS
 * THEM TOGETHER.** `SortableCardTile.tsx` declares
 * `STICKER_DRAG_MEDIA_TYPE = 'application/x-molt-sticker'` — the same value —
 * and `BoardStickerTray` imports THAT one. The two files were written in
 * parallel against `AUDIT.md` §7 and arrived at the same string honestly (it
 * is the house shape: `CardChecklistSection.tsx` ships
 * `'application/x-molt-checklist-item'` one directory over). They agree today
 * and would drift silently the first time either is edited alone, because a
 * mismatch compiles on both sides and simply never completes the gesture —
 * the C3 defect this chunk's audit is headlined by. `CardTile.test.tsx`
 * imports both and asserts they are equal, the way `cn.twmerge.test.ts` holds
 * `styles.css` and `cn.ts` together. **The right resolution is one
 * declaration, and it belongs to whoever may edit both files** — recorded in
 * the returns as a residual rather than fixed here, because importing the
 * sibling's constant would make `CardTile` ↔ `SortableCardTile` a module
 * cycle (that file already imports this one).
 *
 * ⚠ **The marker is load-bearing, not decorative.** The card front's
 * stretched `<a href>` is natively draggable, so a member dragging one card's
 * link across its neighbour fires `dragover` on that neighbour carrying
 * `text/uri-list` and `text/plain`; a drop target that accepted *any* drag
 * while the tray was armed would place a sticker there. `types` is readable
 * during `dragover` (the drag data store is in protected mode, which still
 * exposes the type list), which is exactly why the discriminator lives in the
 * TYPE and not in the payload.
 *
 * Lowercase by construction: the drag-data store lowercases every custom type
 * it is given, so a mixed-case spelling here would never match what
 * `types.includes` reports.
 *
 * The value stored under it is deliberately **not read** by this file — see
 * `onPlaceSticker`'s docstring for why the sticker id does not travel in the
 * drop. Setting the sticker id there anyway is harmless and useful to a human
 * watching a drag in devtools.
 */
export const STICKER_DRAG_TYPE = 'application/x-molt-sticker';

interface BoardShortcutPreferenceState {
  readonly singleKeyShortcutsDisabled: boolean;
  readonly reducedMotion: boolean;
}

const BoardShortcutPreferencesContext = createContext<BoardShortcutPreferenceState>({
  singleKeyShortcutsDisabled: false,
  reducedMotion: false,
});

export function BoardShortcutPreferencesProvider({
  value,
  children,
}: {
  value: BoardShortcutPreferenceState;
  children: ReactNode;
}) {
  return (
    <BoardShortcutPreferencesContext.Provider value={value}>
      {children}
    </BoardShortcutPreferencesContext.Provider>
  );
}

export interface CardTileProps {
  card: BoardFaceList['cards'][number];
  /** Stable list identity for scroll restoration; listTitle remains literal display context. */
  listId?: string | undefined;
  /** B60: board-level mode controls how a non-fresh `agingStage` is drawn. */
  cardAgingMode?: CardAgingMode | undefined;
  /**
   * ⚠ The three props below are chunk `C3`'s (agent E), declared by the parent
   * in the stub pass so that agents F and G compile against the real shape
   * rather than waiting. `C3-BRIEF.md` §6.4 is binding on all three.
   *
   * Quick edit is open on this card — the tile renders its lifted state and
   * hands the content to `QuickEdit`.
   */
  quickEditing?: boolean | undefined;
  /** Opens quick edit. Undefined when the viewer cannot edit: no pencil, no `e`, no right-click. */
  onQuickEdit?: (() => void) | undefined;
  /** Archives the card from the front. Undefined when the viewer cannot edit. */
  onArchive?: (() => void) | undefined;
  /** Toggles card completion from the front. Undefined when the viewer cannot edit. */
  onToggleCompleted?: (() => void) | undefined;
  /**
   * From `useSortable`, applied to the tile ROOT — `FR-BOARD-025` says a card
   * is dragged by grabbing anywhere on its front, so unlike a list there is no
   * separate handle. The tile is therefore a drag source, a navigating anchor
   * and a right-click target at once, held apart only by `PointerSensor`'s
   * `distance: 6`. It is the highest-risk element in the chunk.
   */
  dragHandle?: DragHandle | undefined;
  /** `FR-CARD-009` AC3. Local-only until the product has a persisted setting. */
  showLabelNames?: boolean | undefined;
  /** Route-owned open path. When present, ordinary clicks carry board return state. */
  onOpenCard?: ((intent: BoardCardOpenIntent) => void) | undefined;
  /** Opens this card with the dates popover active. Undefined when dates are not editable. */
  onOpenDates?: ((intent: BoardCardOpenIntent) => void) | undefined;
  /** `FR-BOARD-023` AC2: the card the member returned from stays findable. */
  highlighted?: boolean | undefined;
  /** P2-17: disables card-front single-key shortcuts without changing clicks or modifier shortcuts. */
  singleKeyShortcutsDisabled?: boolean | undefined;
  /** P2-17: suppresses decorative opacity transitions on the card front. */
  reducedMotion?: boolean | undefined;
  /** P2-18: list context for the screen-reader-only card description. */
  listTitle?: string | undefined;
  /** P2-18: zero-based card index in its rendered list. */
  cardIndex?: number | undefined;
  /** P2-18: rendered open-card count for the list. */
  listCardCount?: number | undefined;
  /** B57: selected for a board-face bulk action. */
  selected?: boolean | undefined;
  /** B57: true once the board is already in selection mode. */
  selectionActive?: boolean | undefined;
  /** B57: handles modifier/range/plain replacement selection gestures. */
  onSelectGesture?:
    | ((input: CardSelectionInput, card: BoardFaceList['cards'][number]) => void)
    | undefined;
  /** B57: opens the bulk action surface for a selected card. */
  onOpenBulkActions?: (() => void) | undefined;
  /** B64: removes only this destination-board mirror surface. */
  onRemoveMirror?: ((mirror: CardMirror) => void) | undefined;
  /** B64: converts a link card into an ordinary card. */
  onConvertLinkCard?: ((card: BoardFaceLinkCardSurface) => void) | undefined;
  /** B65: optional template-card state, until board contents carries it natively. */
  templateState?:
    | {
        readonly isTemplate: boolean;
        readonly hiddenFromList: boolean;
        readonly busy?: boolean | undefined;
      }
    | undefined;
  /** B65: hide a visible template card from everyday board flow. */
  onHideTemplateCard?: ((card: BoardFaceList['cards'][number]) => void) | undefined;
  /** B65: unhide a template card wherever a caller renders hidden templates. */
  onUnhideTemplateCard?: ((card: BoardFaceList['cards'][number]) => void) | undefined;
  powerUpSurfaces?: PowerUpSurfaceSummary | undefined;
  pendingSync?: boolean | undefined;
  /**
   * `FR-CARD-025` AC5 — the board's `covers_enabled` setting, projected onto
   * `boardContentsBoardSchema.coversEnabled` by the Card-Front Projection
   * contract chunk and threaded down by the composer.
   *
   * ⚠ **Defaults to `true`, and the default is load-bearing rather than
   * polite.** Every caller that predates the field (this file's own suite,
   * `QuickEdit.test.tsx`, `board.composition.test.tsx`, and the composers
   * until they thread it) omits it, and a card front with no cover setting
   * has always drawn its cover. `?? true` is what keeps those unchanged;
   * `?? false` would blank every cover in the product the moment this landed.
   *
   * ⚠ **This suppresses the WHOLE cover treatment, not just the band.** AC5 is
   * *"card fronts show no covers even for cards that have one set"*, so
   * `cover` collapses to `null` and `fullCover` collapses with it — the badge
   * strip comes back, `min-h-[5.75rem]` goes away, and the title stops
   * borrowing `cover.inkClassName`. That last one is the silent half: a
   * `text-board-ink-light` title over a plain surface is an invisible title,
   * and it is what a partial fix leaves behind.
   *
   * ⚠ **Nothing is filtered on the server, deliberately.**
   * `board-contents.integration.test.ts` rules on it by name — *"project the
   * fact, filter nothing"* (B15's colourless-labels ruling, one table over) —
   * so `card.cover` still arrives whole and suppression is this component's
   * act. Turning the setting back on re-draws the cover from data already in
   * the client, with no refetch.
   */
  coversEnabled?: boolean | undefined;
  /**
   * `FR-CARD-005`'s optional board setting (`docs/notes/card-keys.md`) —
   * `boardContentsBoardSchema.showCardNumbersOnFront`, threaded on
   * `coversEnabled`'s footing. When `true`, `CardTile` draws the card's `ref`
   * (`MG-0001`) muted before the title; a card whose `ref` is `null` (a viewer
   * outside the reference scope) draws nothing.
   */
  showCardRef?: boolean | undefined;
  /** Owner-directed: board's `dangerousComments` threshold; cards at or above it get a red tint. `null`/undefined = off. */
  dangerousComments?: number | null | undefined;
  /**
   * `FR-CARD-032` AC1 / `FR-EXP-027` AC1 — a sticker was dropped on this card
   * front, at these coordinates.
   *
   * ⚠ **It carries no sticker id, on purpose.** `AUDIT.md` §7.1 settles the
   * shape as *"one `place(cardId, x, y)`, two ways in"* — the tray holds the
   * armed sticker because its KEYBOARD path (choose a sticker, choose a card,
   * place) has no drag to carry one, and a pointer path that read the id out
   * of `dataTransfer` would give the two entry points two different payloads
   * for one act. The card front answers **where**, and only where.
   *
   * ⚠ **The coordinates are already wire-legal when they arrive.** This
   * component runs `clampFrontStickerPlacement` before calling — containment
   * inside the tile (the root is `overflow-hidden`, so an unbounded drop is a
   * sticker the member placed and cannot see) and, mandatorily, rounding to
   * four decimals, because a drop coordinate is `clientX − rect.left` and that
   * is fractional under device-pixel-ratio and browser zoom while
   * `stickerPositionSchema` is `decimal(4)`. An unrounded coordinate is a
   * **422 on an ordinary drag**. The caller must pass them through unchanged.
   *
   * ⚠ **That clamp is an INPUT-side act and is NOT a render-time transform**,
   * which is what reconciles it with the sticker layer's own docstring below
   * (*"no scale factor and no front-specific clamp"*). It runs once, before
   * the POST; every reader afterwards — this file, `CardStickerSection`, the
   * other member's browser — still renders `sticker.positionX` verbatim, so
   * `FR-CARD-032` AC4's *"they see it in the same position"* is untouched.
   *
   * Undefined when the viewer cannot place: no drop target, and a sticker drag
   * passes over the card without `preventDefault`, which is what makes the
   * browser refuse the drop rather than accept it silently.
   */
  onPlaceSticker?:
    ((position: { positionX: number; positionY: number }) => void) | undefined;
  /**
   * `FR-CARD-032` AC1 / `FR-EXP-027` AC1 — **the accept ring, driven from
   * outside.** `true` exactly while a sticker drag is over the card that the
   * OUTER drop target has resolved as its receiver. It paints the ring and
   * sets `data-sticker-drop-active`; it carries no coordinate, no sticker id
   * and no handler, and it can post nothing.
   *
   * ─────────────────────────────────────────────────────────────────────
   * ⚠ THE `<li>`-VERSUS-BOOLEAN RULING, SETTLED HERE
   * ─────────────────────────────────────────────────────────────────────
   *
   * `fe-plan.md`'s residual said the ring's home *"has an argument written on
   * both sides in `AUDIT.md` §7"*. `docs/design/fe12-card-front-sticker-ring/AUDIT.md`
   * §2.2 measured that forward reference and it **does not resolve** — §7 of
   * `fe9-card-front-chrome/AUDIT.md` is the three-drag-sources table, and a
   * scan of all 58,744 of its bytes for `outline-accent`,
   * `data-sticker-drop-active`, `stickerDropActive` and `onPlaceSticker`
   * returns 0. The argument was never written on either side; it is
   * constructed and settled here, by measurement.
   *
   * The two candidate homes are both inside `SortableCardTile.tsx`
   * (`AUDIT.md` §3.1 corrects the brief: `ListColumn.tsx` renders no card
   * `<li>` at all): a class on its own `<li>`, or a boolean it computes and
   * forwards into this file. **The boolean wins, on shape.**
   *
   *   1. **`outline` follows the border-radius of the element it is on, and
   *      the two boxes do not have the same radius.** `CardSurface` is
   *      `rounded-card`, `--radius-card: 0.625rem` (`styles.css:87`); the
   *      `<li>` at `SortableCardTile.tsx:508` carries no radius, no
   *      background and no padding. A ring on the `<li>` is therefore a
   *      **square-cornered** ring around a 10px-rounded card, drawn OUTSIDE
   *      the card's box, while `highlighted` and `selected` are drawn
   *      `-outline-offset-2` INSIDE it. Three states that all mean *this
   *      card, right now* would read as two different shapes — and reading
   *      as one shape is this file's own recorded tie-breaker for the ring
   *      (see `stickerDropActive`'s `useState` below), taken from the
   *      product tie-breaker that a board must stay readable in ten seconds.
   *   2. **The class list already exists here and nowhere else.** The render
   *      below carries `highlighted`, `selected` and `stickerDropActive` as
   *      three lines of one `cn()` call. The boolean side adds no class in
   *      any file; the `<li>` side would declare a fourth copy of the same
   *      visual in a second file, where the first restyle would miss it.
   *   3. **The `<li>` is `opacity-0` while dnd-kit drags the card**
   *      (`SortableCardTile.tsx:537`), so a ring painted there is one
   *      `isDragging` away from being invisible for reasons that have
   *      nothing to do with stickers.
   *
   * ⚠ **This is NOT `onPlaceSticker` passed through, and the reason
   * `SortableCardTile` withheld THAT prop stands unchanged.** Two nested
   * native drop targets both calling `preventDefault()` means the inner one
   * swallows the `stickerId` the outer reads off `dataTransfer`. A boolean
   * registers no `dragover`, no `drop` and no `preventDefault` anywhere, so
   * the hazard cannot arise: the outer target stays the only one that
   * accepts, and this file stays the only one that draws.
   *
   * ⚠ **The optimistic branch ignores it, and that is correct.** A
   * placeholder card returns early with neither the attribute nor the ring,
   * for the same reason the handlers are not attached there: no server id
   * exists for a placement to point at. `SortableCardTile`'s `canAct` gate
   * means the composed application never passes `true` to one anyway, so the
   * two refusals agree rather than one masking the other.
   *
   * ⚠ **A class, never a transform.** The ring is `outline`, which paints
   * outside layout and outside the transform stack. `BoardFace`'s
   * `DragOverlay` renders this component as its single measured child, and
   * `getMeasurableNode` measures that child — the recorded `rotate-2` defect
   * that made a card impossible to drag between lists by keyboard. An
   * `outline` moves no box and changes no coordinate system.
   */
  stickerDropActive?: boolean | undefined;
  /**
   * `FR-EXP-027` AC2 — the member hovered a placed sticker and asked to remove
   * it.
   *
   * ⚠ **It REQUESTS; it must not delete.** AC2 is *"hovers it and selects
   * remove **and confirms**"*, and `FR-CARD-032`'s behaviour text says sticker
   * deletion *"has no undo, matching Trello, so it requires a confirmation"*.
   * The composer opens the dialog the card back already ships, under the same
   * `cardBack.stickers.remove.title` / `.description` keys — one confirmation,
   * two entry points. A front control wired straight to
   * `DELETE /card-stickers/:id` would fail AC2 while the card back passed.
   *
   * Undefined when the viewer cannot edit: no control renders at all.
   */
  onRequestRemoveSticker?: ((sticker: PlacedSticker) => void) | undefined;
  /**
   * `FR-CARD-032` behaviour text — a front sticker's rotation or stacking
   * order changed from the card front itself.
   */
  onAdjustSticker?:
    ((sticker: PlacedSticker, body: StickerUpdateBody) => void) | undefined;
}

export interface CardSelectionInput {
  readonly shiftKey?: boolean | undefined;
  readonly metaKey?: boolean | undefined;
  readonly ctrlKey?: boolean | undefined;
}

export function cardAgingClassName({
  mode,
  stage,
  exempt,
}: {
  mode: CardAgingMode | undefined;
  stage: CardAgingStage | null;
  exempt: boolean;
}): string | undefined {
  if (
    exempt ||
    mode === undefined ||
    mode === 'off' ||
    stage === null ||
    stage === 'fresh'
  ) {
    return undefined;
  }

  if (mode === 'regular') {
    switch (stage) {
      case 'week_1':
        return 'opacity-95 saturate-[0.95]';
      case 'week_2':
        return 'opacity-85 saturate-[0.85] contrast-[0.96]';
      case 'week_4':
        return 'opacity-75 saturate-[0.75] contrast-[0.92]';
    }
  }

  switch (stage) {
    case 'week_1':
      return 'border border-dashed border-border-strong bg-surface-raised/95 shadow-[inset_0_0_0_1px_var(--color-border)] sepia-[0.08]';
    case 'week_2':
      return "border border-dashed border-border-strong bg-surface-raised/90 shadow-[inset_0_0_0_1px_var(--color-border)] sepia-[0.16] before:pointer-events-none before:absolute before:inset-1 before:rounded-[6px] before:border before:border-dashed before:border-border before:content-['']";
    case 'week_4':
      return "border border-dashed border-border-strong bg-surface-raised/85 shadow-[inset_0_0_0_1px_var(--color-border-strong)] sepia-[0.24] before:pointer-events-none before:absolute before:inset-1 before:rounded-[6px] before:border before:border-dashed before:border-border-strong before:content-[''] after:pointer-events-none after:absolute after:inset-x-2 after:top-1/2 after:-rotate-2 after:border-t after:border-dashed after:border-border-strong after:content-['']";
  }
}

/**
 * Tokens used below, named against `apps/web/src/lib/density.ts`'s terms —
 * this mapping is this file's half of rule 6's density report:
 *
 * | class            | token                              | density.ts term          |
 * |-------------------|-------------------------------------|---------------------------|
 * | `p-2`              | 8px vertical padding, per edge       | `PROVISIONAL.cardPaddingY`|
 * | `gap-1.5`          | 6px between rows                     | `PROVISIONAL.cardRowGap`  |
 * | `text-card`        | 14.5625px (`--text-card`)            | `SETTLED.cardText`        |
 * | `leading-[1.35]`   | 1.35 line-height multiplier          | `PROVISIONAL.lineHeight`  |
 * | `LabelChip size='sm'` | 20px row (`h-5`)                  | `SETTLED.labelChipHeight` |
 * | `Avatar size='xs'`    | 20px row (`size-5`)                | *no term — see the return*|
 *
 * `leading-[1.35]` is a literal rather than an import because Tailwind's
 * class scanner needs a static string; it is the same number as
 * `PROVISIONAL.lineHeight` by hand, not by reference — density.ts's
 * exported constant is what a re-measure should be checked against.
 *
 * ⚠ None of these classes moved in `C3` — padding, row gap, line height and
 * the avatar row are unchanged (rule 8's density report, §9). What is new is
 * structural only: `group` and `relative` on the root (positioning context
 * and hover/focus-within scoping for the pencil) and conditionally `z-10`
 * (lifted/dragging) — none of which affect the measured box.
 */
function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function cardAccessibleDescription({
  t,
  card,
  listTitle,
  cardIndex,
  listCardCount,
  dueStatus,
  dueAt,
  dueLabel,
  selected,
  powerUpSurfaces,
  pendingSync,
}: {
  // A field of the options object rather than a positional first parameter:
  // this function has exactly one parameter today, and the shape follows
  // `groupMentionOptions` (`lib/comments.ts`). `CardDateTranslate` is
  // structurally identical to the context's `t`, so no cast is needed.
  t: CardDateTranslate;
  card: BoardFaceList['cards'][number];
  listTitle?: string | undefined;
  cardIndex?: number | undefined;
  listCardCount?: number | undefined;
  dueStatus: DueStatus;
  dueAt: string | null;
  dueLabel?: string | undefined;
  selected?: boolean | undefined;
  powerUpSurfaces?: PowerUpSurfaceSummary | undefined;
  pendingSync?: boolean | undefined;
}): string {
  const parts: string[] = [];

  if (selected === true) {
    parts.push(t('board.card.description.selected'));
  }

  if (pendingSync === true) {
    parts.push(t('board.card.description.pendingSync'));
  }

  if (isMirrorCard(card)) {
    parts.push(t('board.card.description.mirrorFrom', { board: card.sourceBoard.title }));
    if (card.sourceDeleted) {
      parts.push(t('board.card.description.sourceDeleted'));
    } else if (card.sourceArchived) {
      parts.push(t('board.card.description.sourceArchived'));
    }
  }

  if (isLinkCardSurface(card)) {
    if (card.linkTargetUnavailable) {
      parts.push(t('board.card.description.linkTargetUnavailable'));
    } else {
      const previewTitle =
        card.linkTargetTitleCache ?? linkCardTargetHostLabel(card.linkTargetUrl);
      const sourceTitle =
        card.linkTargetBoardTitleCache ?? linkCardTargetHostLabel(card.linkTargetUrl);
      parts.push(
        t('board.card.description.linkPreview', {
          title: previewTitle,
          source: sourceTitle,
        }),
      );
    }
  }

  if (listTitle !== undefined && cardIndex !== undefined && listCardCount !== undefined) {
    parts.push(
      t('board.card.description.position', {
        index: cardIndex + 1,
        count: listCardCount,
        list: listTitle,
      }),
    );
  }

  if (card.labels.length > 0) {
    parts.push(
      t('board.card.description.labels', {
        labels: card.labels
          .map((label) => labelChipName(label.colour, label.name, t))
          .join(', '),
      }),
    );
  }

  if (card.hasDescription) {
    parts.push(t('board.card.description.hasDescription'));
  }

  if (card.members.length > 0) {
    parts.push(
      t('board.card.description.members', {
        members: card.members.map(profileName).join(', '),
      }),
    );
  }

  if (card.commentCount > 0) {
    parts.push(
      t(
        'board.card.description.comments',
        { count: card.commentCount },
        card.commentCount,
      ),
    );
  }

  if (card.voteCount !== null && card.voteCount > 0) {
    parts.push(
      t('board.card.description.votes', { count: card.voteCount }, card.voteCount),
    );
  }

  if (card.attachmentCount > 0) {
    parts.push(
      t(
        'board.card.description.attachments',
        { count: card.attachmentCount },
        card.attachmentCount,
      ),
    );
  }

  if (card.checklistItemCount > 0) {
    parts.push(
      t('board.card.description.checklist', {
        checked: card.checklistCheckedItemCount,
        total: card.checklistItemCount,
      }),
    );
  }

  if (dueStatus !== 'none') {
    parts.push(
      dueAt === null
        ? t('board.card.description.dueStatus', {
            status: dueStatusLabel(t, dueStatus),
          })
        : t('board.card.description.dueWithDate', {
            date: dueLabel ?? formatCardDate(t, dueAt),
            status: dueStatusLabel(t, dueStatus),
          }),
    );
  }

  if (card.customFieldBadges.length > 0) {
    parts.push(
      t('board.card.description.customFields', {
        fields: card.customFieldBadges
          .map((badge) =>
            t('board.card.description.customFieldItem', {
              name: badge.name,
              // ⚠ Was `customFieldBadgeValue(badge)` with no formatter, so a
              // `date` badge reached a screen reader as the raw wire
              // timestamp while the VISIBLE badge beside it (`CardBadges`,
              // same wire value) was formatted. Found by making the
              // translator a required parameter; nothing was red.
              value: customFieldBadgeValue(t, badge, (value) => formatCardDate(t, value)),
            }),
          )
          .join(', '),
      }),
    );
  }

  if ((powerUpSurfaces?.cardFrontBadges.length ?? 0) > 0) {
    parts.push(
      t('board.card.description.powerUps', {
        badges: (powerUpSurfaces?.cardFrontBadges ?? [])
          .map((entry) =>
            t('board.card.description.powerUpItem', {
              powerUpName: entry.powerUpName,
              label: entry.label,
            }),
          )
          .join(', '),
      }),
    );
  }

  return parts.join(' ').trim();
}

function isMirrorCard(card: BoardFaceList['cards'][number]): card is CardMirror {
  return 'surfaceType' in card && card.surfaceType === 'mirror';
}

function isLinkCardSurface(
  card: BoardFaceList['cards'][number],
): card is BoardFaceLinkCardSurface {
  return 'surfaceType' in card && card.surfaceType === 'link_card';
}

function linkCardTargetHostLabel(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace(/^www\./, '') || parsed.pathname || url;
  } catch {
    return url;
  }
}

export function CardTile({
  card,
  listId,
  cardAgingMode,
  quickEditing = false,
  onQuickEdit,
  onArchive,
  onToggleCompleted,
  dragHandle,
  showLabelNames = false,
  onOpenCard,
  onOpenDates,
  highlighted = false,
  singleKeyShortcutsDisabled: singleKeyShortcutsDisabledProp,
  reducedMotion: reducedMotionProp,
  listTitle,
  cardIndex,
  listCardCount,
  selected = false,
  selectionActive = false,
  onSelectGesture,
  onOpenBulkActions,
  onRemoveMirror,
  onConvertLinkCard,
  templateState,
  onHideTemplateCard,
  onUnhideTemplateCard,
  powerUpSurfaces,
  pendingSync = false,
  coversEnabled = true,
  showCardRef = false,
  dangerousComments = null,
  onPlaceSticker,
  stickerDropActive: stickerDropActiveProp = false,
  onRequestRemoveSticker,
  onAdjustSticker,
}: CardTileProps) {
  const shortcutPreferences = useContext(BoardShortcutPreferencesContext);
  const localePreferences = useLocalePreferencesValue();
  const { t } = useInterfaceLanguage();
  const singleKeyShortcutsDisabled =
    singleKeyShortcutsDisabledProp ?? shortcutPreferences.singleKeyShortcutsDisabled;
  const reducedMotion = reducedMotionProp ?? shortcutPreferences.reducedMotion;
  const title = cardDisplayTitle(t, card.title);
  const optimistic = isOptimisticCardId(card.id);
  const mirrorCard = isMirrorCard(card);
  const linkCard = isLinkCardSurface(card);
  const ordinaryFieldActions = !mirrorCard && !linkCard;
  const isTemplateCard = templateState?.isTemplate === true;
  const hiddenTemplateCard = isTemplateCard && templateState.hiddenFromList;
  const visibleLabels = frontLabels(card.labels);

  // §5.4: all three openers are withheld together — no edit permission, a
  // placeholder card, or quick edit already open on this one.
  const canOpenQuickEdit =
    !optimistic && ordinaryFieldActions && !quickEditing && onQuickEdit !== undefined;
  const canArchive = !optimistic && !quickEditing && onArchive !== undefined;
  const canToggleCompleted =
    !optimistic &&
    ordinaryFieldActions &&
    !quickEditing &&
    onToggleCompleted !== undefined;
  const canOpenDates =
    !optimistic && ordinaryFieldActions && !quickEditing && onOpenDates !== undefined;
  const canOpenBulkActions =
    !optimistic && !quickEditing && selected && onOpenBulkActions !== undefined;
  const [mirrorExpanded, setMirrorExpanded] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const dueStatus = resolveDueStatus({
    dueAt: card.dueAt,
    completed: card.completed,
    now,
  });
  // `FR-CARD-025` AC5. Collapsing `cover` to `null` here — rather than gating
  // each of the three render sites below — is what makes `fullCover` collapse
  // with it, because its own initialiser still ends in `cover !== null`. One
  // edit, and the badge strip, the minimum height and the title's ink class
  // all follow. See `coversEnabled`'s docstring for why the partial fix is the
  // dangerous one.
  //
  // FE-80.1 card-cover-tail — ⚠ `t` is threaded here as well as at
  // `CardCoverSection.tsx:79`, and it has to be BOTH. `coverVisual` has exactly
  // two non-test callers and they render two different surfaces (this file is
  // the board front, that one the card back) off one helper, so routing at one
  // caller leaves the other printing English `Green cover` with nothing red
  // anywhere. `card-cover.routing.test.ts` pins both call sites by source text
  // for exactly that reason.
  const cover = coversEnabled ? coverVisual(t, card.cover) : null;
  const dueLabel =
    card.dueAt === null
      ? undefined
      : formatLocaleCardDueDate(t, card.dueAt, localePreferences, card.dueAllDay);
  const fullCover = card.cover?.size === 'full' && cover !== null;
  const dangerous = dangerousComments !== null && card.commentCount >= dangerousComments;
  const agingClassName = cardAgingClassName({
    mode: cardAgingMode,
    stage: card.agingStage,
    exempt: card.agingExempt,
  });
  const activeAgingStage =
    agingClassName === undefined || card.agingStage === null
      ? undefined
      : card.agingStage;
  /**
   * `FR-CARD-032` AC1/AC4 and `FR-EXP-027` — the placements
   * `boardFaceCardSchema.stickers` now projects (`FE-58.1`).
   *
   * ⚠ **Filtered to `'front'` even though the board-face projection is already
   * front-only.** Statement 12 of `board-contents.ts` reads
   * `surface = 'front'` and `ck_card_stickers_surface` makes that a closed
   * two-valued choice, so this filter drops nothing the server sends. It is
   * kept for two reasons: it is the shipped idiom on the sibling surface
   * (`CardStickerSection` filters to `'back'` off the same shared element),
   * and this array is also written client-side by TanStack cache updates — a
   * front sticker tray landing later writes board contents optimistically, and
   * an unfiltered render would put a back-surface placement on the card front
   * with nothing failing.
   *
   * ⚠ **Rendered in array order with NO `z-index` on any sticker.**
   * `boardFaceCardSchema.stickers`' own docstring settles it: the read is
   * `ORDER BY cs.z_index, cs.id`, so *the array's order is the stacking
   * order and a client renders it back to front without sorting*. Copying
   * `zIndex` onto the elements would be worse than redundant — the values are
   * signed `integer`s and a negative one would escape this overlay's flow and
   * paint underneath the tile's own background.
   */
  const frontStickers = card.stickers.filter((sticker) => sticker.surface === 'front');
  /**
   * `FR-EXP-027` AC2's remove control, held in a `const` rather than read off
   * the destructured parameter at the call site: TypeScript re-widens a
   * narrowed *parameter* inside a closure created later, so
   * `onRequestRemoveSticker !== undefined ? … onClick={() => onRequest…()}`
   * does not compile. A `const` narrows once and stays narrowed.
   */
  const requestRemoveSticker = onRequestRemoveSticker;
  const requestAdjustSticker = onAdjustSticker;
  /**
   * `FR-CARD-032` AC1 / `FR-EXP-027` AC1 — the card front as a drop target,
   * **as seen by THIS file's own handlers**. Only ever `true` while a drag
   * carrying `STICKER_DRAG_TYPE` is over this tile AND `onPlaceSticker` was
   * given, so it costs nothing on a resting board. The ring it feeds draws
   * the same `outline-accent` `highlighted` and `selected` already use rather
   * than a new token: three states that all mean *this card, right now*
   * reading as one shape is the tie-breaker applied, and it adds no
   * `--text-*` token and so no `cn.ts` registration.
   *
   * Renamed off `stickerDropActive` when the accept ring gained its second,
   * outer source; the ring itself is the `const` below, and every render site
   * reads THAT. A handler here must still test this half alone — the outer
   * source is not ours to clear.
   */
  const [selfDropActive, setSelfDropActive] = useState(false);
  /**
   * ⚠ **Two sources, one ring, and an `||` rather than a `??`.** Both halves
   * mean the same thing — *a sticker drag is over this card right now* — so
   * either being `true` lights it, and neither may switch the other off.
   * `stickerDropActiveProp ?? selfDropActive` would read a composer's honest
   * `false` (the ordinary state on every card the drag is NOT over) as a
   * reason to ignore this file's own target, which is how a ring that works
   * in isolation stops working the moment it is composed.
   *
   * In the composed application `selfDropActive` is always `false`, because
   * no composer passes `onPlaceSticker` to this file — `ListColumn.tsx:676`
   * passes it to `SortableCardTile`, whose `<li>` is the target that reads
   * the `stickerId`. This file's own target is what a `CardTile` rendered
   * WITHOUT that wrapper falls back to (`BoardFace`'s `DragOverlay` is the
   * only such site today), and its 13 leaf tests below still hold it.
   */
  const stickerDropActive = stickerDropActiveProp || selfDropActive;
  const descriptionId = useId();
  const accessibleDescription = cardAccessibleDescription({
    t,
    card,
    listTitle,
    cardIndex,
    listCardCount,
    dueStatus,
    dueAt: card.dueAt,
    dueLabel,
    selected,
    powerUpSurfaces,
    pendingSync,
  });
  const describedBy = accessibleDescription.length > 0 ? descriptionId : undefined;
  const dragDescribedBy = dragHandle?.attributes['aria-describedby'];
  const combinedDescribedBy =
    [describedBy, dragDescribedBy]
      .filter((value): value is string => Boolean(value))
      .join(' ') || undefined;

  const isHoveredRef = useRef(false);
  const rightClickSuppressedRef = useRef(false);
  const cardLinkRef = useRef<HTMLAnchorElement | null>(null);

  // §7.2: the HOVERED half of "hovered or focused, focus winning if both".
  // A document-level listener, but only while this specific card is under the
  // pointer — not one permanent listener per card. The FOCUSED half needs no
  // such listener at all: it is the plain `onKeyDown` below, which only ever
  // fires while this tile (or its pencil) holds focus, because a keydown
  // bubbles from wherever focus actually is. This handler defers to that path
  // by checking `document.activeElement`: if anything but `<body>` is
  // focused, some element's own bubbling handler already had first refusal —
  // firing again here would double-open (if it was this card) or override the
  // focused card (if it was another one), which is exactly what "focus wins"
  // forbids.
  //
  // ⚠ For `FR-BOARD-022`'s browser spec (agent H): this precondition is
  // literally `document.activeElement === document.body` — so the hovered
  // path only fires when NOTHING has been focused yet in that page. A test
  // that clicks or tabs anywhere first (even to dismiss something) and THEN
  // hovers a card and presses 'e' will see nothing happen, correctly, unless
  // it also blurs whatever it focused. This is deliberate, not a bug to work
  // around: `document.activeElement` cannot distinguish "a member deliberately
  // left focus on the search field while hovering a card" from "a member
  // never touched the keyboard" — treating the first case as fair game for
  // hover would be indistinguishable, from inside this component, from
  // stealing 'e' out from under a focused control.
  useEffect(() => {
    if (!canOpenQuickEdit && !canArchive && !canOpenDates && !canOpenBulkActions) {
      return undefined;
    }

    function handleDocumentKeyDown(event: globalThis.KeyboardEvent) {
      if (!isHoveredRef.current) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTextEntryTarget(event.target)) return;
      if (singleKeyShortcutsDisabled) return;
      const active = document.activeElement;
      if (active !== null && active !== document.body) return;
      if (event.key === 'e' && canOpenBulkActions) {
        event.preventDefault();
        onOpenBulkActions?.();
        return;
      }
      if (event.key === 'e' && canOpenQuickEdit) {
        // ⚠ Without this the `e` that OPENS quick edit is also typed into it:
        // React flushes a discrete keydown synchronously, so the field is mounted,
        // focused and select-all'd before this keystroke's default text insertion
        // runs — which then replaces the whole title with `e`, and `commitOn="enter"`
        // will save it. `handleKeyDown` (the focused half of §7.2) always did this;
        // the hovered half did not, and only a browser can hover.
        event.preventDefault();
        onQuickEdit?.();
        return;
      }
      if (event.key.toLowerCase() === 'd' && canOpenDates) {
        event.preventDefault();
        openDatesFromElement(cardLinkRef.current);
        return;
      }
      if (event.key.toLowerCase() !== 'c' || !canArchive) return;
      // Keep the browser from treating this as ordinary text input if focus
      // moves during the mutation response; the shortcut owns the keystroke.
      event.preventDefault();
      onArchive?.();
    }

    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown);
  }, [
    canArchive,
    canOpenBulkActions,
    canOpenDates,
    canOpenQuickEdit,
    onArchive,
    onOpenBulkActions,
    onQuickEdit,
    onOpenDates,
    singleKeyShortcutsDisabled,
  ]);

  useEffect(() => {
    if (card.dueAt === null || card.completed) return undefined;
    const interval = window.setInterval(() => {
      setNow(new Date());
    }, 60_000);
    return () => window.clearInterval(interval);
  }, [card.completed, card.dueAt]);

  function handleKeyDown(event: KeyboardEvent<Element>) {
    /*
     * ⚠ `dragHandle.listeners.onKeyDown` (dnd-kit's `KeyboardSensor`
     * activator, verified against `core.esm.js`: its `eventName` really is
     * `'onKeyDown'`) is called explicitly here, FIRST, rather than left to a
     * JSX spread. A prop declared after a spread of the same name wins in
     * JSX, so spreading `dragHandle?.listeners` after this handler would
     * silently replace it — dead 'e' handling in the shipped app, invisible
     * to any test that renders `CardTile` without a `dragHandle`. Calling it
     * by hand keeps both: Space/Escape still drive the keyboard sensor
     * (§7.5, decided in `lib/dnd.ts`, not here), and 'e' still opens quick
     * edit.
     */
    dragHandle?.listeners?.onKeyDown?.(event);

    if (
      onSelectGesture !== undefined &&
      !optimistic &&
      event.key === 'Enter' &&
      (event.metaKey || event.ctrlKey || event.shiftKey)
    ) {
      event.preventDefault();
      onSelectGesture(
        {
          shiftKey: event.shiftKey,
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey,
        },
        card,
      );
      return;
    }

    if (!canOpenQuickEdit && !canArchive && !canOpenDates && !canOpenBulkActions) {
      return;
    }
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (isTextEntryTarget(event.target)) return;
    if (singleKeyShortcutsDisabled) return;
    if (event.key === 'e' && canOpenBulkActions) {
      event.preventDefault();
      onOpenBulkActions?.();
      return;
    }
    if (event.key === 'e' && canOpenQuickEdit) {
      event.preventDefault();
      onQuickEdit?.();
      return;
    }
    if (event.key.toLowerCase() === 'd' && canOpenDates) {
      event.preventDefault();
      openDatesFromElement(cardLinkRef.current);
      return;
    }
    if (event.key.toLowerCase() === 'c' && canArchive) {
      event.preventDefault();
      onArchive?.();
    }
  }

  // §7.3: a plain right-click opens quick edit and suppresses the native
  // menu; a modified one (Shift/Ctrl/Cmd) or a second consecutive plain one
  // lets the native menu through — the escape hatch, toggled per tile.
  function handleContextMenu(event: MouseEvent<Element>) {
    if (canOpenBulkActions) {
      event.preventDefault();
      rightClickSuppressedRef.current = true;
      onOpenBulkActions?.();
      return;
    }
    if (!canOpenQuickEdit) return;
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      rightClickSuppressedRef.current = false;
      return;
    }
    if (rightClickSuppressedRef.current) {
      rightClickSuppressedRef.current = false;
      return;
    }
    event.preventDefault();
    rightClickSuppressedRef.current = true;
    onQuickEdit?.();
  }

  /*
   * ⚠ A named callback rather than `ref={dragHandle?.setActivatorNodeRef}`
   * directly. Belt-and-braces here (the anchor below is a plain intrinsic
   * element, not `CardSurface`, so `exactOptionalPropertyTypes` is less
   * likely to bite the way it did on `CardSurface`'s own narrower `ref?:` —
   * see that file's docstring), but the same shape of problem is cheap to
   * pre-empt: `dragHandle?.setActivatorNodeRef` IS `undefined` whenever
   * `dragHandle` is absent, and this wrapper is never itself `undefined` —
   * only its effect is a no-op then — so the prop's value stays a function
   * regardless of how strictly the target types it.
   */
  function setActivatorRef(element: HTMLAnchorElement | null) {
    cardLinkRef.current = element;
    dragHandle?.setActivatorNodeRef(element);
  }

  function handlePencilClick(event: MouseEvent<HTMLButtonElement>) {
    // The pencil sits above the stretched link (see the file header) but a
    // click still bubbles through this component's own tree; nothing here
    // stops it reaching the link's own click handling by default, so this is
    // belt-and-braces rather than load-bearing.
    event.preventDefault();
    event.stopPropagation();
    onQuickEdit?.();
  }

  function handleCompletionClick(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    onToggleCompleted?.();
  }

  /**
   * A sticker drag, and nothing else. See `STICKER_DRAG_TYPE` for why the
   * discriminator is the `dataTransfer` TYPE and not its payload.
   */
  function isStickerDrag(event: DragEvent<HTMLElement>): boolean {
    return event.dataTransfer.types.includes(STICKER_DRAG_TYPE);
  }

  /**
   * ⚠ **`preventDefault()` is what makes this element a drop target.** The
   * HTML drag protocol's default action on `dragover` is *refuse the drop*, so
   * a target that only sets state accepts nothing — and the failure is
   * silent: the cursor shows "no drop", `drop` never fires, and no test that
   * looks at handlers rather than at the gesture can see it. It is therefore
   * called ONLY once both guards pass, which is also how a viewer without
   * placement permission refuses a sticker without any code that says "no".
   */
  function handleStickerDragOver(event: DragEvent<HTMLElement>): void {
    if (onPlaceSticker === undefined || !isStickerDrag(event)) return;

    /*
     * ⚠ **DERIVED from the drag source's `effectAllowed`, never asserted.**
     * A `dropEffect` incompatible with the source's `effectAllowed` makes the
     * user agent force the drag operation to `none` and fire **no `drop`
     * event at all** — so a hard-coded `'copy'` against a tray that set
     * `effectAllowed = 'move'` (which is what `CardChecklistSection.tsx`, the
     * nearest precedent a tray author would copy, does) is a drop target that
     * never fires, in a browser, behind a green unit suite: jsdom negotiates
     * nothing. `SortableCardTile`'s outer target derives it the same way and
     * runs after this one on the same event; the two must not disagree.
     */
    const allowed = event.dataTransfer.effectAllowed;
    if (allowed === 'none') return;
    event.dataTransfer.dropEffect =
      allowed === 'move' || allowed === 'linkMove'
        ? 'move'
        : allowed === 'link'
          ? 'link'
          : 'copy';

    event.preventDefault();
    if (!selfDropActive) setSelfDropActive(true);
  }

  /**
   * ⚠ **`dragleave` fires on this element when the pointer enters a CHILD of
   * it** — and this tile is full of children: the stretched link, the label
   * chips, the badge strip, an already-placed sticker. Clearing the state
   * unconditionally would strobe the ring on every internal crossing. The
   * `relatedTarget` (the element being entered) is checked against this
   * subtree instead. `instanceof Node` is the narrow React's
   * `EventTarget | null` needs before `contains` will take it.
   */
  function handleStickerDragLeave(event: DragEvent<HTMLElement>): void {
    if (!selfDropActive) return;
    const entering = event.relatedTarget;
    if (entering instanceof Node && event.currentTarget.contains(entering)) return;
    setSelfDropActive(false);
  }

  /**
   * ⚠ **The coordinate is `clientX − rect.left` against THIS element's box,
   * never `event.offsetX`.** `offset*` is relative to the event TARGET, which
   * here is whichever of the stretched anchor, a `LabelChip`, the badge strip
   * or an existing sticker `<img>` happens to be topmost under the pointer —
   * so it produces plausible-looking wrong numbers, the worst kind. The
   * tile's own root is `event.currentTarget` because that is where these
   * handlers are attached.
   *
   * The wire value is the CLAMPED one; see `onPlaceSticker`'s docstring for
   * why the clamp is an input-side act and not a render-time transform.
   *
   * ⚠ **`preventDefault()` here is also what stops the drop being taken
   * TWICE.** `SortableCardTile` carries an outer target on the `<li>` that
   * wraps this tile, a native drop bubbles, and that handler opens with
   * `if (event.defaultPrevented) return;` — naming "a future per-sticker
   * target inside `CardTile`" as the winner in as many words. So the
   * precedence is defined rather than accidental: whichever of the two is
   * wired, exactly one placement is posted, and when both are wired the inner
   * one wins — which is the correct one, because the coordinate origin both
   * files agree on is THIS element's box.
   */
  function handleStickerDrop(event: DragEvent<HTMLElement>): void {
    setSelfDropActive(false);
    if (onPlaceSticker === undefined || !isStickerDrag(event)) return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    onPlaceSticker(
      clampFrontStickerPlacement({
        positionX: event.clientX - rect.left,
        positionY: event.clientY - rect.top,
        tileWidth: rect.width,
        tileHeight: rect.height,
      }),
    );
  }

  function nextStickerRotation(sticker: PlacedSticker): number {
    return Number(((sticker.rotation + 15) % 360).toFixed(2));
  }

  function nextStickerStackIndex(
    sticker: PlacedSticker,
    direction: 'forward' | 'backward',
  ): number {
    const indexes = frontStickers.map((entry) => entry.zIndex);
    if (direction === 'forward') return Math.max(...indexes, sticker.zIndex) + 1;
    return Math.min(...indexes, sticker.zIndex) - 1;
  }

  function requestStickerAdjustment(
    event: MouseEvent<HTMLButtonElement>,
    sticker: PlacedSticker,
    body: StickerUpdateBody,
  ) {
    event.preventDefault();
    event.stopPropagation();
    requestAdjustSticker?.(sticker, body);
  }

  function handleTemplateVisibilityClick(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (hiddenTemplateCard) {
      onUnhideTemplateCard?.(card);
    } else {
      onHideTemplateCard?.(card);
    }
  }

  function handleOpenCardClick(event: MouseEvent<HTMLAnchorElement>) {
    if (
      onSelectGesture !== undefined &&
      !optimistic &&
      event.button === 0 &&
      (event.metaKey || event.ctrlKey || event.shiftKey || selectionActive)
    ) {
      event.preventDefault();
      event.stopPropagation();
      onSelectGesture(
        {
          shiftKey: event.shiftKey,
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey,
        },
        card,
      );
      return;
    }

    if (onOpenCard === undefined) return;
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    onOpenCard(openIntentFromElement(event.currentTarget));
  }

  function openIntentFromElement(
    element: HTMLElement | null,
    openPanel?: BoardCardOpenIntent['openPanel'],
  ): BoardCardOpenIntent {
    const cardStack = element?.closest<HTMLElement>('[data-board-card-stack-list-id]');
    const stackMatchesCard =
      listId === undefined || cardStack?.dataset.boardCardStackListId === listId;
    return {
      cardId: card.id,
      path: card.path,
      stripScrollLeft: 0,
      listScrollTop: stackMatchesCard ? (cardStack?.scrollTop ?? 0) : 0,
      openPanel,
    };
  }

  function openDatesFromElement(element: HTMLElement | null) {
    onOpenDates?.(openIntentFromElement(element, 'dates'));
  }

  /*
   * Member initials (`docs/notes/member-initials.md`) — the avatar row's lift.
   *
   * ⚠ **WHY THE ROW IS ABOVE THE STRETCHED LINK.** The link is `absolute
   * inset-0 z-[2]` and covers the whole front, so before this nothing under it
   * — the avatars included — could be hovered, and the circles' tooltips
   * (`CardBadges`, `names="tooltip"`) would never open. `relative z-[3]` puts
   * the row (and only the row: it is the `AvatarStack` root, a few circles
   * wide) one level above the link, the same move the placed stickers' hover
   * controls make (`FR-EXP-027`, the sticker layer below). It is a z-index and
   * NEVER a transform: a transform here would sit inside `BoardFace`'s
   * `DragOverlay` child and change the geometry dnd-kit measures (`CLAUDE.md`).
   * No `pointer-events-*` is needed — nothing above the row turns them off.
   *
   * ⚠ It works because the row's ancestors make no stacking context: the
   * content wrapper is `relative` at `z-index: auto`. On a full-cover front the
   * wrapper is `z-[1]` and WOULD trap the row under the link — but a full-cover
   * front renders no badge row (`!fullCover` below), so the case never arises.
   * Give that wrapper a z-index on a front with badges and the tooltips die
   * silently.
   *
   * ⚠ **UNLIKE THE STICKER CONTROLS, THE ROW SWALLOWS NOTHING.** Those
   * controls stop their clicks because each is its own action; an avatar is not
   * one, so everything a pointer does on the link must still happen on the row:
   *   • drag — dnd-kit's pointer activators (`onMouseDown`, `onTouchStart`;
   *     every listener but the keyboard one, which the row never receives
   *     because it is not focusable) are spread here as well as on the link, so
   *     a drag that starts on a circle moves the card. dnd-kit's own
   *     document-level click suppression after a drag covers this row too.
   *   • click — re-dispatched to the link as a cancelable click carrying the
   *     same button and modifiers, so `handleOpenCardClick` runs with the link
   *     as `currentTarget` (the open intent, the selection gesture) and a link
   *     with no `onOpenCard` still navigates natively.
   *   • middle click — `auxclick`, which the re-dispatched `click` never
   *     sees, opens the link's address in a new tab (`onAuxClick`).
   *     ⚠ Unmeasured: whether a re-dispatched (untrusted) cmd/ctrl-click
   *     opens a new tab in every browser when no `onSelectGesture` claims it.
   *   • right-click — `handleContextMenu`, the link's own.
   *   • hover — `isHoveredRef`, which the link's `mouseleave` clears the
   *     moment the pointer crosses onto the row, would otherwise switch off the
   *     hovered `e`/`c`/`d` shortcuts over the avatars.
   * Keyboard is untouched: the link stays the one focusable activator.
   *
   * Not on an optimistic card: it has no link to cover the row.
   */
  const memberRowProps: ComponentPropsWithoutRef<'span'> | undefined = optimistic
    ? undefined
    : {
        className: 'relative z-[3]',
        ...memberRowDragActivators(dragHandle?.listeners),
        onClick: (event) => {
          const link = cardLinkRef.current;
          if (link === null) return;
          event.preventDefault();
          event.stopPropagation();
          link.dispatchEvent(
            new globalThis.MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              composed: true,
              detail: event.detail,
              button: event.button,
              buttons: event.buttons,
              clientX: event.clientX,
              clientY: event.clientY,
              screenX: event.screenX,
              screenY: event.screenY,
              altKey: event.altKey,
              ctrlKey: event.ctrlKey,
              metaKey: event.metaKey,
              shiftKey: event.shiftKey,
            }),
          );
        },
        // A middle click fires `auxclick`, not `click`, and a re-dispatched
        // (untrusted) click would not open a tab anyway — so the row opens
        // the link's own address in a new tab, as a middle click on the rest
        // of the front does.
        onAuxClick: (event) => {
          const link = cardLinkRef.current;
          if (link === null || event.button !== 1) return;
          event.preventDefault();
          event.stopPropagation();
          globalThis.open(link.href, '_blank', 'noopener');
        },
        onContextMenu: handleContextMenu,
        onMouseEnter: () => {
          isHoveredRef.current = true;
        },
        onMouseLeave: () => {
          isHoveredRef.current = false;
        },
      };

  const content = (
    <>
      {mirrorCard ? (
        <MirrorDisplayControls
          card={card}
          expanded={mirrorExpanded}
          onExpandedChange={setMirrorExpanded}
          onRemove={onRemoveMirror ? () => onRemoveMirror(card) : undefined}
        />
      ) : null}
      {linkCard ? (
        <LinkCardChrome
          card={card}
          onConvert={onConvertLinkCard ? () => onConvertLinkCard(card) : undefined}
        />
      ) : null}
      {cover !== null && !fullCover ? (
        <div
          aria-label={cover.label}
          className="h-10 shrink-0 rounded-[6px] border border-border-strong bg-board bg-cover bg-center"
          style={cover.style}
        />
      ) : null}
      {isTemplateCard ? (
        <div className="flex flex-wrap items-center gap-1">
          <span className="inline-flex h-5 items-center gap-1 rounded-card border border-accent/40 bg-accent/10 px-1.5 text-xs font-semibold text-ink">
            <LayoutTemplate aria-hidden="true" className="size-3" />
            {t('board.card.templateBadge')}
          </span>
          {hiddenTemplateCard ? (
            <span className="inline-flex h-5 items-center rounded-card border border-border px-1.5 text-xs font-medium text-ink-muted">
              {t('board.card.hiddenFromListBadge')}
            </span>
          ) : null}
        </div>
      ) : null}
      {pendingSync ? (
        <span className="inline-flex w-fit items-center rounded-card border border-accent/40 bg-accent/10 px-1.5 text-xs font-semibold text-ink">
          {t('board.card.pendingSyncBadge')}
        </span>
      ) : null}
      {visibleLabels.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {visibleLabels.map((label) => (
            <LabelChip
              key={label.id}
              colour={label.colour}
              name={label.name}
              showName={showLabelNames}
              size="sm"
            />
          ))}
        </div>
      ) : null}
      <div
        className={cn(
          'text-card leading-[1.35] line-clamp-2 font-medium',
          fullCover ? cover.inkClassName : 'text-ink',
        )}
      >
        {/*
          `FR-CARD-005`'s board setting (on by default since `0070`) — the
          card's reference (docs/notes/card-keys.md, "Web"). It rides at the
          right end of the badge row so the title keeps its whole line; only a
          full-cover front, which has no badge row, keeps it inline before the
          title. The reference is data; the anchor's accessible name stays the
          title.
        */}
        {fullCover && showCardRef && card.ref !== null ? (
          <span
            data-card-ref
            className="mr-1 text-xs font-normal tabular-nums opacity-80"
          >
            {card.ref}
          </span>
        ) : null}
        {/*
          Its own inline element, so the title is still a node whose text is
          exactly the title when a full cover puts the reference beside it — a
          bare text node next to the span would leave no element to match it.
        */}
        <span data-card-title>{title}</span>
      </div>
      {!fullCover ? (
        <CardBadges
          card={card}
          dueAt={card.dueAt}
          dueAllDay={card.dueAllDay}
          dueStatus={dueStatus}
          powerUpSurfaces={powerUpSurfaces}
          cardRef={showCardRef ? card.ref : null}
          dangerous={dangerous}
          memberRowProps={memberRowProps}
        />
      ) : null}
    </>
  );

  if (optimistic) {
    // §5.3: no anchor (there is no `path` yet), no drag, no quick edit.
    return (
      <CardSurface
        as="div"
        variant="sheet"
        aria-label={title}
        aria-busy="true"
        data-card-id={card.id}
        data-board-list-id={listId}
        className="flex flex-col gap-1.5 overflow-hidden p-2"
      >
        {content}
      </CardSurface>
    );
  }

  return (
    <CardSurface
      as="div"
      variant="sheet"
      interactive
      data-card-id={card.id}
      data-board-list-id={listId}
      data-card-aging-mode={activeAgingStage === undefined ? undefined : cardAgingMode}
      data-card-aging-stage={activeAgingStage}
      data-dangerous={dangerous ? 'true' : undefined}
      data-board-card-highlighted={highlighted ? 'true' : undefined}
      data-board-card-selected={selected ? 'true' : undefined}
      /*
       * ⚠ **The attribute and the ring below read the COMBINED
       * `stickerDropActive`, never `selfDropActive`** — this is the one line
       * that makes the accept ring reachable in the composed application,
       * where the target that resolves the card is `SortableCardTile`'s
       * `<li>` and this file's own handlers never fire. A reader that
       * regressed to the local state would put the ring back behind a prop
       * no composer passes: green leaf suite, dead board.
       */
      data-sticker-drop-active={stickerDropActive ? 'true' : undefined}
      /*
       * `FR-CARD-032` AC1 / `FR-EXP-027` AC1 — the card front is A drop
       * target, and these three handlers are the whole of it. They cost ZERO
       * pixels: no element, no padding, no class that affects layout, so
       * `density-budget.md`'s 24 cards at 1440×900 and its 2.4px of headroom
       * are unchanged.
       *
       * ⚠ **Not attached to the optimistic branch above**, deliberately: a
       * placeholder card has no server id for a placement to point at, so a
       * drop on one could not be posted anywhere.
       */
      onDragOver={handleStickerDragOver}
      onDragLeave={handleStickerDragLeave}
      onDrop={handleStickerDrop}
      className={cn(
        'group relative flex flex-col gap-1.5 overflow-hidden p-2',
        agingClassName,
        dangerous && !fullCover && 'border border-bad/40 bg-bad/10',
        dangerous && fullCover && 'ring-2 ring-inset ring-bad/60',
        fullCover && 'min-h-[5.75rem] justify-end',
        highlighted && 'outline outline-2 -outline-offset-2 outline-accent',
        selected && 'outline outline-2 -outline-offset-2 outline-accent',
        stickerDropActive && 'outline outline-2 -outline-offset-2 outline-accent',
        quickEditing && 'z-10',
      )}
    >
      {/*
        ⚠ **Everything interactive lives on THIS element, not the root** —
        drag activator, `aria-label`, and every handler below. That was NOT
        true of the first version of this file, and the reason it changed is
        a real, verified defect rather than a style preference:
        `KeyboardSensor.activators` (`core.esm.js`, ~line 1343) does
        `if (activator && event.target !== activator) return false` — a
        STRICT reference check against `active.activatorNode.current`, i.e.
        against whatever `setActivatorNodeRef` marked. `dragHandle.attributes`
        also carries `tabIndex: 0` (`DraggableAttributes`, all six fields
        required), so putting the whole bundle on the outer `<div>` — with
        `aria-label` left on this inner, empty anchor — made TWO separately
        focusable, differently-named stops per card: a `role="button"` div
        with NO accessible name (nothing overrides name-from-content, so it
        would have accumulated the label/title/avatar text — the exact
        C1 label-folding defect this file's own header already warns about,
        reintroduced one element over) that Space would activate but Enter
        would do nothing on, and a named link that Enter would follow but
        Space would not drag (`event.target` would be the anchor, not the
        activator div, so dnd-kit's check fails silently). One focusable,
        named element that is BOTH the activator and the link is the only
        shape where Space drags and Enter opens the SAME thing a member just
        tabbed to. `SortableCardTile.test.tsx` and this file's own drag-handle
        tests were rewritten once this was found; see the return for how it
        was caught (a stronger review, not a test — the unit suite could not
        have failed on this, since every test either checked the root's
        attributes directly or the anchor's role/name in isolation, never
        both "is this the same element" at once).
        The only child is a screen-reader description wired by
        `aria-describedby`, so the link name stays the title while metadata is
        still announced. `absolute inset-0` keeps it out of flow so the rows
        below are unaffected in size or position — `rounded-card` matches the
        card's own corner so a focus ring drawn on it reads as one shape with
        it.
        ⚠ `-outline-offset-2` on focus, not the global default: the root's
        `overflow-hidden` clips an outline drawn OUTSIDE this element's edge
        (the browser default), and this anchor's edge IS the root's edge —
        pulling the ring 2px inward keeps it inside the clip and visible.
        ⚠ **Reasoning, not a screenshot** — the clipping argument follows from
        `overflow-hidden` + an edge-to-edge `absolute inset-0` child, but
        whether this class actually renders the ring correctly (and whether
        Tailwind emits `-outline-offset-2` from this codebase's config at
        all) wants the parent's screenshot pass, same as the pencil below.
      */}
      <a
        href={card.path}
        aria-label={title}
        ref={setActivatorRef}
        className={cn(
          'absolute inset-0 z-[2] rounded-card focus-visible:-outline-offset-2',
        )}
        {...dragHandle?.attributes}
        {...dragHandle?.listeners}
        /*
         * Declared AFTER the spreads above, same reasoning as before the
         * restructure: a later prop of the same name wins in JSX, so these
         * props must come last or dnd-kit's own `aria-describedby`/`onKeyDown`
         * (dnd-kit's `KeyboardSensor` activator) would silently replace
         * the card metadata or `handleKeyDown` in the shipped app.
         * `handleKeyDown` still calls `dragHandle?.listeners?.onKeyDown?.(event)`
         * itself, by hand, so both keep running.
         */
        aria-describedby={combinedDescribedBy}
        onMouseEnter={() => {
          isHoveredRef.current = true;
        }}
        onMouseLeave={() => {
          isHoveredRef.current = false;
        }}
        onKeyDown={handleKeyDown}
        onContextMenu={handleContextMenu}
        onClick={handleOpenCardClick}
      >
        {accessibleDescription.length > 0 ? (
          <span id={descriptionId} className="sr-only">
            {accessibleDescription}
          </span>
        ) : null}
      </a>

      {fullCover ? (
        <div
          aria-label={cover.label}
          className="pointer-events-none absolute inset-0 rounded-card border border-border-strong bg-board bg-cover bg-center"
          style={cover.style}
        />
      ) : null}

      {/*
        `FR-CARD-032`/`FR-EXP-027` — the card-front sticker layer.

        ⚠ **A SIBLING OF THE STRETCHED LINK, NEVER A CHILD OF IT.** The anchor
        above is deliberately empty apart from its screen-reader description;
        this file's own header records the C1 defect where content folded into
        that anchor's accessible name, and a named `alt` inside it would read
        as that defect reintroduced even though the explicit `aria-label`
        overrides name-from-content.

        ⚠ **DOM ORDER IS THE WHOLE LAYERING RULE, AND IT IS THE REQUIREMENT.**
        `FR-EXP-027`'s edge case: *"the card title layer stays above them"*.
        This layer carries no `z-index`, so among positioned siblings at
        `z-index: auto` the later one paints on top — it sits AFTER the
        full-cover div (stickers are visible over a cover, `FR-CARD-032` AC1 is
        unconditional) and BEFORE the content wrapper, so the title, labels and
        badges all paint over it. Moving this block below the content wrapper,
        or giving it any `z-index` at all, inverts that and breaks the edge
        case with nothing red. The stretched link stays above everything at
        `z-[2]`, and `pointer-events-none` here keeps clicks, drags and the
        pencil reaching what they reached before.

        ⚠ **`rotate` GOES ON THE STICKER, NEVER ON THE TILE.** A CSS transform
        on the outermost child of a `DragOverlay` puts the dragged item in a
        different coordinate system from every droppable it is measured
        against — the recorded `rotate-2` defect that made a card impossible to
        drag between lists by keyboard. A transform on a descendant does not
        affect an ancestor's measured box, so this is the safe half of that
        rule, the same half `BoardFace` uses.

        ⚠ **DENSITY: THIS COSTS ZERO PIXELS.** `absolute inset-0` is out of
        flow, so the tile's measured box is byte-identical to what it was
        before — no padding, no gap, no new row. `density-budget.md` prices the
        headroom at 2.4px for 24 cards at 1440×900 and this layer does not
        spend it.

        ⚠ **`left`/`top` are the wire's own pixels from THIS box's origin.**
        No scale factor and no front-specific clamp: `sticker.ts`'s docstring
        rests `FR-CARD-032` AC4 (*"they see it in the same position"*) on one
        shared element, and a client-side transform of the coordinates would
        make the front and the back disagree about a placement — the exact
        drift the shared element exists to prevent. `size-8` rather than the
        card back's `size-12` is a render size, not a coordinate: the back is a
        144px editing canvas and this is the product surface the tie-breaker
        protects.

        ⚠ **Assistive technology, `FR-EXP-027`'s first edge case verbatim** —
        *"hidden from assistive technology unless they carry a name the user
        set"*. `alt` is the sticker's own name, so no interface-language key is
        owed here (the string is data, not copy); an unnamed sticker takes an
        empty `alt` AND `aria-hidden`, which is the card back's shipped idiom.

        Not rendered on the optimistic branch above: a placeholder card has no
        server id for a placement to point at, so its `stickers` is always
        `[]`.
      */}
      {frontStickers.length > 0 ? (
        <div className="pointer-events-none absolute inset-0">
          {frontStickers.map((sticker) => (
            /*
              ⚠ A `Fragment` and not a wrapper element. The `<img>` must stay a
              DIRECT child of the layer above — `left`/`top` are absolute
              against the layer's box, and this file's own suite pins the
              layer by `getByAltText(…).parentElement`. A positioned wrapper
              would move both the coordinate origin and that assertion's
              subject at once.
            */
            <Fragment key={sticker.id}>
              <img
                src={stickerImageSrc(sticker.sticker.renderUrl)}
                alt={sticker.sticker.name ?? ''}
                aria-hidden={sticker.sticker.name === null ? true : undefined}
                className="absolute size-8 object-contain"
                style={{
                  left: sticker.positionX,
                  top: sticker.positionY,
                  rotate: `${sticker.rotation}deg`,
                }}
              />
              {requestAdjustSticker === undefined ? null : (
                <div
                  className={cn(
                    'pointer-events-none absolute z-20 flex rounded-card border border-border-strong bg-surface-raised shadow-card opacity-0',
                    'group-hover:pointer-events-auto group-hover:opacity-100',
                    !reducedMotion && 'transition-opacity',
                  )}
                  style={{
                    left: `min(${sticker.positionX + STICKER_FRONT_RENDER_PX + 2}px, calc(100% - 5.25rem))`,
                    top: sticker.positionY,
                  }}
                >
                  <IconButton
                    label={t('cardBack.stickers.rotate', {
                      sticker: stickerLabel(t, sticker.sticker),
                    })}
                    size="sm"
                    variant="quiet"
                    tabIndex={-1}
                    onClick={(event) =>
                      requestStickerAdjustment(event, sticker, {
                        rotation: nextStickerRotation(sticker),
                      })
                    }
                    className="rounded-none first:rounded-l-card"
                  >
                    <RotateCw aria-hidden="true" className="size-3.5" />
                  </IconButton>
                  <IconButton
                    label={t('cardBack.stickers.bringForward', {
                      sticker: stickerLabel(t, sticker.sticker),
                    })}
                    size="sm"
                    variant="quiet"
                    tabIndex={-1}
                    onClick={(event) =>
                      requestStickerAdjustment(event, sticker, {
                        zIndex: nextStickerStackIndex(sticker, 'forward'),
                      })
                    }
                    className="rounded-none"
                  >
                    <BringToFront aria-hidden="true" className="size-3.5" />
                  </IconButton>
                  <IconButton
                    label={t('cardBack.stickers.sendBackward', {
                      sticker: stickerLabel(t, sticker.sticker),
                    })}
                    size="sm"
                    variant="quiet"
                    tabIndex={-1}
                    onClick={(event) =>
                      requestStickerAdjustment(event, sticker, {
                        zIndex: nextStickerStackIndex(sticker, 'backward'),
                      })
                    }
                    className="rounded-none last:rounded-r-card"
                  >
                    <SendToBack aria-hidden="true" className="size-3.5" />
                  </IconButton>
                </div>
              )}
              {requestRemoveSticker === undefined ? null : (
                /*
                  `FR-EXP-027` AC2 — *"hovers it and selects remove"*.

                  ⚠ **A SIBLING OF THE `<img>`, NEVER A CHILD OF IT.**
                  An unnamed sticker's image carries `aria-hidden` (the
                  requirement's first edge case); a control inside that subtree
                  would be stripped from the accessibility tree entirely —
                  visible to a mouse and absent to assistive technology, with
                  nothing red. `<img>` is void, so "sibling" is the only shape
                  available, and this comment exists so it stays that way.

                  ⚠ **The layer stays `pointer-events-none`; only THIS element
                  turns it back on.** Removing it from the layer instead would
                  put a full-bleed transparent box over every card front and
                  make **every card undraggable, with no test failing** — the
                  layer is `absolute inset-0` and sits above the tile's own
                  content. `pointer-events` is inherited and re-enabling it on
                  one descendant is the whole fix.

                  ⚠ **Paired with the opacity, not just hidden.** `opacity-0`
                  still takes clicks and `group-hover` never fires on touch, so
                  a bare `opacity-0 group-hover:opacity-100` would leave an
                  invisible destructive target on every placed sticker on a
                  phone. The pointer events are gated on the same hover.

                  ⚠ **`tabIndex={-1}` is a ruling, not an oversight** —
                  `AUDIT.md` §7.1/§8. A focusable control per placed sticker
                  would add tab stops across up to 24 cards on a board the
                  product tie-breaker says must stay readable in ten seconds.
                  The KEYBOARD path for AC2 is the card back, which already
                  ships `useRemoveSticker` behind the same confirmation. The
                  control keeps its accessible name and stays reachable in a
                  screen reader's browse mode; only the tab ring is withheld.

                  Its box IS the sticker's box — `size-8` overriding
                  `IconButton`'s `size-7` through `cn`, at the sticker's own
                  `left`/`top`, so no second copy of the 32px number and no
                  arithmetic is introduced here, and the control can never
                  reach past the sticker onto the title. It takes the sticker's
                  position and not its `rotate`: a tilted hit area would be
                  harder to hit than the thing it removes.
                */
                <IconButton
                  label={t('cardBack.stickers.removeLabel', {
                    sticker: stickerLabel(t, sticker.sticker),
                    title,
                  })}
                  size="sm"
                  variant="on-scrim"
                  tabIndex={-1}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    requestRemoveSticker(sticker);
                  }}
                  className={cn(
                    'pointer-events-none absolute z-10 size-8 rounded-full bg-overlay opacity-0',
                    'group-hover:pointer-events-auto group-hover:opacity-100',
                    !reducedMotion && 'transition-opacity',
                  )}
                  style={{ left: sticker.positionX, top: sticker.positionY }}
                >
                  <X aria-hidden="true" className="size-3.5" />
                </IconButton>
              )}
            </Fragment>
          ))}
        </div>
      ) : null}

      {/*
       * The completion toggle below is `absolute left-1 top-1` over a `p-2`
       * box: `left-1` (4px) + `size-7` (28px) means its BOX — it paints
       * `hover:bg-surface-raised` across all 28px, not just the 14px glyph —
       * ends at x=32, while this column starts at x=8. Without a gutter the
       * circle lands on whatever the first row renders: the title's first
       * character on a plain card, a label chip on a labelled one.
       *
       * ⚠ **The gutter is reserved on the SAME condition that renders the
       * button, never on hover.** A completed card paints the toggle at
       * `opacity-100` — that collision is permanent, not a hover state, so
       * restyling the button (an opaque chip, a shadow) would leave a green
       * check sitting on the title forever. Reserving on hover instead would
       * reflow the title under the cursor, and reserving on `completed`
       * alone would jump the text sideways the moment a card is ticked.
       * Boards that disable the front control (`FR-CARD-013`'s last
       * sentence) render neither the button nor the gutter and pay nothing.
       *
       * `pl-7` (28px) puts the column edge at x=36, 4px clear of the button.
       * It costs horizontal space only — no row is taller — but a narrower
       * column wraps a long title sooner, which is the density face of this
       * (`density-budget.md`); the pencil at `right-1 top-1` is deliberately
       * NOT given the matching `pr-7`, because two gutters take ~56px off a
       * ~256px column and that is where a one-line title becomes two.
       */}
      <div
        className={cn(
          'relative flex flex-col gap-1.5',
          (canToggleCompleted || card.completed) && 'pl-7',
          fullCover && 'z-[1]',
        )}
      >
        {content}
      </div>

      {canToggleCompleted || card.completed ? (
        <IconButton
          label={
            card.completed
              ? t('board.card.markIncomplete', { title })
              : t('board.card.markComplete', { title })
          }
          size="sm"
          onClick={handleCompletionClick}
          disabled={!canToggleCompleted}
          className={cn(
            'absolute left-1 top-1 z-10',
            !reducedMotion && 'transition-opacity',
            card.completed
              ? 'text-ok opacity-100'
              : 'text-ink-muted opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
          )}
        >
          {card.completed ? (
            <CheckCircle2 aria-hidden="true" className="size-3.5" />
          ) : (
            <Circle aria-hidden="true" className="size-3.5" />
          )}
        </IconButton>
      ) : null}

      {canOpenQuickEdit && (
        /*
         * ⚠ **Unverified: `right-1 top-1` may sit over the label row on a
         * labelled card.** The label row is the first thing `content` below
         * renders, flush with the top of the padded box, and this corner is
         * the same corner. It is hidden (`opacity-0`) until hover/focus, so
         * there is no ALWAYS-visible collision, but a hover state that
         * overlaps a label chip's own name wants a look in a browser, not
         * reasoned about here — see the return.
         */
        <IconButton
          label={t('board.card.quickEdit', { title })}
          size="sm"
          onClick={handlePencilClick}
          className={cn(
            'absolute right-1 top-1 z-10 opacity-100',
            !reducedMotion && 'transition-opacity',
            'md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100',
          )}
        >
          <Pencil aria-hidden="true" className="size-3.5" />
        </IconButton>
      )}
      {isTemplateCard &&
      (onHideTemplateCard !== undefined || onUnhideTemplateCard !== undefined) ? (
        <IconButton
          label={
            hiddenTemplateCard
              ? t('board.card.showInList', { title })
              : t('board.card.hideFromList', { title })
          }
          size="sm"
          onClick={handleTemplateVisibilityClick}
          disabled={
            hiddenTemplateCard
              ? onUnhideTemplateCard === undefined
              : onHideTemplateCard === undefined
          }
          className={cn(
            'absolute bottom-1 right-1 z-10 opacity-100',
            !reducedMotion && 'transition-opacity',
            'md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100',
          )}
        >
          {hiddenTemplateCard ? (
            <Eye aria-hidden="true" className="size-3.5" />
          ) : (
            <EyeOff aria-hidden="true" className="size-3.5" />
          )}
        </IconButton>
      ) : null}
    </CardSurface>
  );
}

/**
 * Member initials — the drag listeners the avatar row re-uses (see
 * `memberRowProps` in `CardTile`): every dnd-kit activator but the keyboard
 * one. Filtered by name rather than picked (`onMouseDown`, `onTouchStart`), so
 * a sensor `lib/dnd.ts` adds later reaches the row without an edit here; the
 * keyboard activator is left out because the row is not focusable and the
 * link already owns it.
 */
function memberRowDragActivators(
  listeners: DragHandle['listeners'] | undefined,
): ComponentPropsWithoutRef<'span'> {
  if (listeners === undefined) return {};
  return Object.fromEntries(
    Object.entries(listeners).filter(([eventName]) => eventName !== 'onKeyDown'),
  );
}
